import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { createApp } from 'vue'
import App from './App.vue'
// Axios is used to fetch data.
import axios from 'axios'
import VueAxios from 'vue-axios'

// Using built-in URL API instead of Node.js url module

import apiPropertyFetcher from './apiPropertyFetcher'
import { validateAndLog } from '@/utils/validation'

// These styles load Tailwind CSS styles.
import './assets/styles/index.css'

// This pattern has been modified so we can alter the routes.
import { createRouter, createWebHashHistory } from 'vue-router'
import routes from './router'
import { validateUUIDParam } from './router/guards'

// See `public/index.html` for an example of the embeddable code.
const appID = 'content-library'
const appElement = document.getElementById(appID)

async function getHomeRoute() {
  let homeRoute = {
    path: '/',
    name: 'Home',
  }

  // Setting the route using query parameters:
  // Node ID: Pass `cl_type=node` and `cl_item={node id}`
  // Node IDs: Pass `cl_type=prescribed` and `cl_items={node id 1}|{node id 2}|...`
  // Type and UUID: Pass `cl_type={content library type}` and `cl_uuid`
  const urlParams = new URLSearchParams(window.location.search)
  const initialQuery = Object.fromEntries(urlParams)

  if (typeof initialQuery.cl_type === 'undefined') {
    homeRoute.component = () => import('@/components/ContentCollection.vue')
    homeRoute.props = {
      uuid: appElement.getAttribute('data-id'),
    }
  }
  else if (initialQuery.cl_type === 'prescribed' && typeof initialQuery.cl_items !== 'undefined') {
    let items = []

    await Promise
      .all(
        initialQuery.cl_items.split('|').map(( nodeID ) => {
          // Validate node ID and skip invalid ones
          const validation = validateAndLog('nodeId', nodeID);
          if (!validation.valid) {
            console.error('Invalid node ID:', nodeID);
            return null;
          }
          return apiPropertyFetcher({ nodeID })
        })
      )
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('error: ', error)
        }
      })
      .then(( data ) => {
        items = data
          .filter((element) => typeof element === 'object')
          .map(({ uuid, contentLibraryType }) => {
            return {
              uuid,
              type: contentLibraryType,
            }
          })

        return data
      })

    if (items.length > 0) {
      homeRoute.component = () => import('@/components/ContentList.vue')
      homeRoute.props = {
        items,
      }
    }
  }
  else if (initialQuery.cl_type === 'node' && typeof initialQuery.cl_item !== 'undefined') {
    // Validate node ID first
    const validation = validateAndLog('nodeId', initialQuery.cl_item);
    if (!validation.valid) {
      homeRoute.component = () => import('@/components/ErrorPage.vue')
      homeRoute.props = {
        message: 'Invalid node ID provided.',
      }
      return homeRoute
    }
    
    await apiPropertyFetcher({ nodeID: initialQuery.cl_item })
      .then( ({ uuid, contentLibraryType }) => {
        // Determine the component from the routes.
        routes.forEach((route) => {
          if (route.path.split('/')[1] === contentLibraryType) {
            homeRoute.component = route.component
          }
        })

        homeRoute.props = {
          uuid,
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('error: ', error)
        }
      })
  }
  // The use case where a type matches the Content Library type and a UUID is provided.
  else if (typeof initialQuery.cl_uuid !== 'undefined') {
    let uuid = initialQuery.cl_uuid
    let type = initialQuery.cl_type
    
    // Validate UUID and block if invalid
    const validation = validateAndLog('uuid', uuid)
    if (!validation.valid) {
      homeRoute.component = () => import('@/components/ErrorPage.vue')
      homeRoute.props = {
        message: 'Invalid UUID provided.',
      }
      return homeRoute
    }

    // Determine the component from the routes.
    routes.forEach((route) => {
      if (route.path.split('/')[1] === type) {
        homeRoute.component = route.component
      }
    })

    homeRoute.props = {
      uuid: uuid,
    }
  }
  
  if (typeof homeRoute.component === 'undefined') {
    homeRoute.component = () => import('@/components/ErrorMessage.vue')
    homeRoute.props = {
      message: 'Content could not be found.',
    }
  }

  return homeRoute
}

// We are using promises to parse the various entrypoints, but we need to wait on those in order to create the application.
const initializeApp = async () => {
  // Add error route
  routes.push({
    path: '/error',
    name: 'Error',
    component: () => import('@/components/ErrorPage.vue'),
    props: true
  })
  
  routes.push(await getHomeRoute())

  const router = createRouter({
    history: createWebHashHistory(),
    routes
  })
  
  // Add global navigation guard for UUID validation
  router.beforeEach(validateUUIDParam)

  // If we want to pass properties to the application, such as a theme class, we can do so here.
  const rootProperties = {}

  createApp(App, rootProperties)
    .use(router)
    .use(VueAxios, axios, router)
    .mount(`#${appID}`)
}

initializeApp()
